<script setup>
/**
 * SidebarSearch Component
 * Modern search input for filtering sidebar navigation.
 */
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const isFocused = ref(false)

function handleInput(e) {
  emit('update:modelValue', e.target.value)
}

function clearSearch() {
  emit('update:modelValue', '')
  inputRef.value?.focus()
}

function handleFocus() {
  isFocused.value = true
}

function handleBlur() {
  isFocused.value = false
}
</script>

<template>
  <div class="sidebar-search" :class="{ focused: isFocused, 'has-value': modelValue }">
    <div class="search-input-wrapper">
      <!-- Search Icon -->
      <svg
        class="search-icon"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
      </svg>

      <!-- Input -->
      <input
        ref="inputRef"
        type="text"
        class="search-input"
        :value="modelValue"
        placeholder="חיפוש בתפריט..."
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <!-- Clear Button -->
      <button
        v-if="modelValue"
        type="button"
        class="clear-btn"
        aria-label="נקה חיפוש"
        @click="clearSearch"
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.sidebar-search {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  transition: all var(--transition-fast);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  transition: all var(--transition-fast);
}

.sidebar-search.focused .search-input-wrapper {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.sidebar-search.has-value .search-input-wrapper {
  background: var(--bg-secondary);
}

.search-icon {
  flex-shrink: 0;
  color: var(--text-muted);
  transition: color var(--transition-fast);
}

.sidebar-search.focused .search-icon {
  color: var(--color-primary);
}

.search-input {
  flex: 1;
  min-width: 0;
  padding: var(--spacing-xs) 0;
  border: none;
  background: transparent;
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  color: var(--text-primary);
  outline: none;
  text-align: right;
}

.search-input::placeholder {
  color: var(--text-muted);
  opacity: 0.7;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.clear-btn:hover {
  background: var(--color-error);
  color: white;
}

.clear-btn:active {
  transform: scale(0.95);
}
</style>
